import { Badge, Button, Code, Group, Skeleton, Stack, Text, Title } from "@mantine/core";
import { useCallback, useState, type ReactNode } from "react";

import { useDailyState } from "../hooks/useDailyState";
import { useDraftSentence } from "../hooks/useDraftSentence";
import { useScorer } from "../hooks/useScorer";
import type { Puzzle } from "../puzzle";
import { failingGates } from "../scoring";
import { bestCanonicalBlend } from "../storage";
import { AttemptBoard } from "./AttemptBoard";
import { BlendDisplay } from "./BlendDisplay";
import { Guidance, type GuidanceState } from "./Guidance";
import { SentenceInput } from "./SentenceInput";
import { ShareCard } from "./ShareCard";

export interface PuzzleGameProps {
  /// The puzzle to play, or `undefined` while it's being derived.
  readonly puzzle: Puzzle | undefined;
  /// Page heading — "today's puzzle" for the daily route.
  readonly heading: string;
  /// Accessible name for the blend list (passed to {@link BlendDisplay}).
  readonly blendLabel: string;
  /// Leading text on the meta line (the daily route shows the puzzle's
  /// resolved date; the random route shows a friendlier seed label).
  /// The "· best blend" suffix is appended by this component.
  readonly metaLabel: (puzzle: Puzzle) => ReactNode;
  /// Route-specific controls (e.g. "play a random blend"), rendered
  /// between the blend and the compose box.
  readonly actions?: ReactNode;
  /// Storage key for attempts/drafts. Defaults to `puzzle.id`. The
  /// daily route passes `puzzle.resolvedFor` so b2-fallback days share
  /// state with yesterday's fresh resolution.
  readonly storageId?: string | undefined;
  /// When `true`, render a "stale" badge — the daily route flips this
  /// on whenever the service served a stale-if-error cached response.
  readonly stale?: boolean;
}

/**
 * The whole game surface: blend display, compose box, scoring, guidance,
 * attempt board, and share card. Everything that varies between the
 * daily and random-blend routes comes in as props; everything else —
 * including the storage/draft keying off `puzzle.id` — is identical, so
 * the two routes share one code path.
 */
export function PuzzleGame({
  puzzle,
  heading,
  blendLabel,
  metaLabel,
  actions,
  storageId,
  stale = false,
}: PuzzleGameProps) {
  const scorerState = useScorer();
  const effectiveStorageId = storageId ?? puzzle?.id;
  const { attempts, addAttempt } = useDailyState(effectiveStorageId);
  const {
    sentence,
    setSentence: setSentenceRaw,
    clear: clearDraftSentence,
  } = useDraftSentence(effectiveStorageId);

  const [guidance, setGuidance] = useState<GuidanceState | undefined>(undefined);
  const [scoring, setScoring] = useState(false);

  // Wrap the draft setter so any edit dismisses stale guidance — once
  // the player starts changing the sentence, the previous failure
  // hints are no longer about what's in the textarea.
  const setSentence = useCallback(
    (s: string) => {
      setSentenceRaw(s);
      setGuidance(undefined);
    },
    [setSentenceRaw],
  );

  const best = bestCanonicalBlend(attempts);

  const canSubmit =
    puzzle !== undefined &&
    scorerState.kind === "ready" &&
    sentence.trim().length > 0 &&
    !scoring;

  const handleSubmit = useCallback(() => {
    if (puzzle === undefined || scorerState.kind !== "ready") return;
    setScoring(true);
    setGuidance(undefined);
    // Scoring is sync, but defer to a microtask so React can flash the
    // loading state on the button before the WASM call blocks the
    // main thread.
    void Promise.resolve().then(() => {
      try {
        const encoded = scorerState.scorer.encodeSentence(sentence);
        if (encoded.status !== "success") {
          setGuidance({ kind: "encode-failed", reason: encoded.status });
          return;
        }
        // Verbose mode: slots 0..31 of `scorerOutputs` are the 32 hard
        // gate results, so a failure surfaces the exact gates that
        // didn't pass.
        const score = scorerState.scorer.scoreVerbose({
          rawSentenceBytes: encoded.rawSentenceBytes,
          bip39WordIndices: puzzle.blendIndices,
          blendSeed: puzzle.blendSeed,
        });
        if (!score.gatesPass) {
          setGuidance({ kind: "gates-failed", failed: failingGates(score.scorerOutputs) });
          return;
        }
        addAttempt({
          sentence,
          canonicalBlend: score.canonicalBlend,
          submittedAt: Date.now(),
        });
        clearDraftSentence();
      } finally {
        setScoring(false);
      }
    });
  }, [addAttempt, clearDraftSentence, puzzle, scorerState, sentence]);

  return (
    <Stack gap="md" maw={720}>
      <Title order={2}>{heading}</Title>
      {puzzle ? (
        <>
          <Group gap="xs" align="center">
            <Text c="dimmed" size="sm">
              {metaLabel(puzzle)}
              {best !== undefined && (
                <>
                  {" · best blend "}
                  <Code>{best.toString()}</Code>
                </>
              )}
            </Text>
            {stale && (
              <Badge color="yellow" variant="light" size="sm">
                stale
              </Badge>
            )}
          </Group>
          {stale && (
            <Text c="dimmed" size="xs">
              showing a cached puzzle while the service recovers.
            </Text>
          )}
          <BlendDisplay blend={puzzle.blend} label={blendLabel} />
        </>
      ) : (
        <Skeleton h={36} w={400} />
      )}

      {actions}

      <SentenceInput
        value={sentence}
        onChange={setSentence}
        onSubmit={canSubmit ? handleSubmit : undefined}
        disabled={scoring}
      />

      <Group>
        <Button onClick={handleSubmit} disabled={!canSubmit} loading={scoring}>
          submit
        </Button>
        {scorerState.kind === "loading" && (
          <Text c="dimmed" size="sm">
            loading scorer…
          </Text>
        )}
        {scorerState.kind === "error" && (
          <Text c="red" size="sm">
            scorer failed to load: {scorerState.error.message}
          </Text>
        )}
      </Group>

      <Guidance guidance={guidance} />

      <AttemptBoard attempts={attempts} />

      {puzzle && effectiveStorageId && attempts.length > 0 && (
        <ShareCard puzzleId={effectiveStorageId} attempts={attempts} />
      )}
    </Stack>
  );
}
